import { Box, createStyles, getStylesRef } from '@mantine/core';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

export interface HorizontalResizeHandleProps {
	side?: 'right' | 'left';
	handleSize?: number;
	minWidth: number;
	maxWidth: number;
	backgroundColor?: string;
	onResize: (updatedWidth: number) => void;
	className?: string;
	onDoubleClick?: () => void;
	onStartDragging?: () => void;
	onStopDragging?: () => void;
	builtinResizeDivider?: boolean;
	hasMidShape?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			side,
			width,
			backgroundColor,
		}: { side: 'left' | 'right'; width: number; backgroundColor: string }
	) => ({
		wrapper: {
			position: 'absolute',
			zIndex: 20,
			cursor: 'col-resize',
			backgroundColor,
			width,
			height: '100vh',
			left: side === 'left' ? 0 : 'auto',
			right: side === 'right' ? 0 : 'auto',
		},
		builtinResizeDivider: {
			...(side === 'left'
				? {
						borderLeft: `1px solid ${theme.other.getColor('border/secondary/default')}`,

						'&:hover': {
							borderLeftColor: theme.other.getColor('border/secondary/hover'),
						},
						'&:active': {
							borderLeftColor: theme.other.getColor('border/secondary/active'),
						},
					}
				: {
						borderRight: `1px solid ${theme.other.getColor('border/secondary/default')}`,

						'&:hover': {
							borderRightColor: theme.other.getColor('border/secondary/hover'),
						},
						'&:active': {
							borderRightColor: theme.other.getColor('border/secondary/active'),
						},
					}),
		},
		midShape: {
			ref: getStylesRef('midShape'),
			position: 'absolute',
			top: 'calc(50% - 20px)',
			transform: 'translate(-50%, -50%)',
			width: Math.max(4, width - 4),
			height: 40,
			backgroundColor: theme.other.getColor('surface/app/default'),
			borderRadius: theme.other.borderRadius.sm,
			opacity: 1,
			border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			pointerEvents: 'none',
			zIndex: 21,
		},
		wrapperHover: {
			'&:hover .midShape': {
				borderColor: theme.other.getColor('border/secondary/hover'),
			},
			'&:active .midShape': {
				borderColor: theme.other.getColor('border/secondary/active'),
			},
		},
	})
);

function HorizontalResizeHandle({
	side = 'right',
	handleSize = 8,
	minWidth,
	backgroundColor = 'transparent',
	onResize,
	onDoubleClick,
	onStartDragging,
	onStopDragging,
	className,
	builtinResizeDivider,
	hasMidShape,
}: HorizontalResizeHandleProps) {
	const { classes, cx } = useStyles({
		side,
		width: handleSize,
		backgroundColor,
	});

	const [clickXPosition, setClickXPosition] = useState(Infinity);

	useEffect(() => {
		if (clickXPosition === Infinity) {
			return () => {};
		}

		const handleMouseMove = (e: MouseEvent) => {
			e.preventDefault();

			let offset = e.clientX;
			// It's more difficult to calculate the width when
			// the handle is at the right side of the window.
			if (side === 'left') {
				offset = window.innerWidth - e.clientX;
			}

			if (offset < 0 || offset < minWidth) {
				return;
			}

			onResize(offset);
			onStartDragging?.();
		};

		const handleMouseUp = (e: MouseEvent) => {
			e.preventDefault();
			setClickXPosition(Infinity);
			document.body.style.cursor = 'auto';
			onStopDragging?.();
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [clickXPosition, minWidth, onDoubleClick, onResize, side]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setClickXPosition(e.clientX);
		document.body.style.cursor = 'col-resize';
	}, []);

	return (
		<Box
			className={cx(
				classes.wrapper,
				builtinResizeDivider && classes.builtinResizeDivider,
				hasMidShape && builtinResizeDivider && classes.wrapperHover,
				className
			)}
			onMouseDown={handleMouseDown}
			onDoubleClick={onDoubleClick}
		>
			{hasMidShape && <Box className={cx(classes.midShape, 'midShape')} />}
		</Box>
	);
}

export default HorizontalResizeHandle;
